import { useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Chatbot from "@/components/Chatbot";
import TranslateButton from "@/components/TranslateButton";

// Route-level code splitting for faster initial load
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const TeacherPanel = lazy(() => import("@/pages/TeacherPanel"));
const ManagerPanel = lazy(() => import("@/pages/ManagerPanel"));
const ClassPage = lazy(() => import("@/pages/ClassPage"));
const MaterialPage = lazy(() => import("@/pages/MaterialPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const MLibrary = lazy(() => import("@/pages/MLibrary"));
const PortfolioPage = lazy(() => import("@/pages/PortfolioPage"));
const BranchesPage = lazy(() => import("@/pages/BranchesPage"));
const BranchDetail = lazy(() => import("@/pages/BranchDetail"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
import LiveBackground from "@/components/LiveBackground";

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0A14]">
    <div className="w-10 h-10 border-2 border-[#EF4444] border-t-transparent rounded-full animate-spin" />
  </div>
);

function AuthCallback() {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");

    if (sessionId) {
      processGoogleSession(sessionId)
        .then((userData) => {
          const target = sessionStorage.getItem('post_login_redirect');
          if (target) { sessionStorage.removeItem('post_login_redirect'); navigate(target, { replace: true }); }
          else navigate("/dashboard", { replace: true, state: { user: userData } });
        })
        .catch(() => {
          navigate("/auth", { replace: true });
        });
    } else {
      navigate("/auth", { replace: true });
    }
  }, [processGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505]">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#A3A3A3] font-light">Authenticating...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A14]">
        <div className="w-10 h-10 border-2 border-[#EF4444] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (allowedRoles) {
    const userRoles = user.roles || [user.role || 'student'];
    // Permanent admin bypass
    if (user.email === 'chandrashekharkolhe7@gmail.com') return children;
    if (!allowedRoles.some(r => userRoles.includes(r))) return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/course/:courseId" element={<CourseDetail />} />
        <Route path="/class/:courseId" element={<ProtectedRoute><ClassPage /></ProtectedRoute>} />
        <Route path="/class/:courseId/material/:materialId" element={<ProtectedRoute><MaterialPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><MLibrary /></ProtectedRoute>} />
        <Route path="/portfolio/:userId" element={<PortfolioPage />} />
        <Route path="/branches" element={<BranchesPage />} />
        <Route path="/branches/:branchId" element={<BranchDetail />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPanel /></ProtectedRoute>} />
        <Route path="/teacher" element={<ProtectedRoute allowedRoles={["teacher", "admin"]}><TeacherPanel /></ProtectedRoute>} />
        <Route path="/manager" element={<ProtectedRoute allowedRoles={["manager", "admin"]}><ManagerPanel /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LiveBackground />
        <div className="app-content">
          <Toaster position="top-center" richColors theme="dark" />
          <AppRouter />
          <TranslateButton />
          <Chatbot />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
